/**
 * Bulk Invoice Operations Component
 * Manages bulk invoice operations with proper API integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { enhancedInvoiceService } from '../../services/enhancedInvoiceService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from "../common/ProfessionalLoader";
import BulkOperationCard from './BulkOperationCard';
import BulkOperationForm from './BulkOperationForm';
import BulkOperationDetails from './BulkOperationDetails';
import './BulkInvoiceOperations.css';

const BulkInvoiceOperations = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageInvoices } = useRBAC();
  
  // State management
  const [operations, setOperations] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard', 'list', 'form', 'details'  
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    operation_type: '',
    page: 1,
    page_size: 20
  });

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await enhancedInvoiceService.getBulkOperationsDashboard();
      
      if (response.success) {
        setDashboardData(response.data);
      } else {
        showError(response.message || 'Failed to load dashboard data', 'Error');
      }
    } catch (error) {
      showError('Failed to load dashboard data', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Load operations list
  const loadOperations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await enhancedInvoiceService.getBulkOperations(filters);
      
      if (response.success) {
        setOperations(response.data.results || []);
      } else {
        showError(response.message || 'Failed to load operations', 'Error');
      }
    } catch (error) {
      showError('Failed to load operations', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [filters, showError]);

  // Load data on component mount
  useEffect(() => {
    if (viewMode === 'dashboard') {
      loadDashboardData();
    } else {
      loadOperations();
    }
  }, [viewMode, loadDashboardData, loadOperations]);

  // Handle operation selection
  const handleOperationSelect = (operation) => {
    setSelectedOperation(operation);
    setViewMode('details');
  };

  // Handle operation approval
  const handleOperationApproval = async (operationId) => {
    setIsProcessing(true);
    try {
      const response = await enhancedInvoiceService.approveBulkOperation(operationId);
      
      if (response.success) {
        showSuccess('Operation approved successfully', 'Success');
        loadOperations();
      } else {
        showError(response.message || 'Failed to approve operation', 'Error');
      }
    } catch (error) {
      showError('Failed to approve operation', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle operation cancellation
  const handleOperationCancellation = async (operationId) => {
    setIsProcessing(true);
    try {
      const response = await enhancedInvoiceService.cancelBulkOperation(operationId);
      
      if (response.success) {
        showSuccess('Operation cancelled successfully', 'Success');
        loadOperations();
      } else {
        showError(response.message || 'Failed to cancel operation', 'Error');
      }
    } catch (error) {
      showError('Failed to cancel operation', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  };

  if (isLoading) {
    return (
      <div className="bulk-invoice-operations">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-invoice-operations">
      {/* Header */}
      <div className="operations-header">
        <div className="header-content">
          <h2>🔄 Bulk Invoice Operations</h2>
          <p>Manage bulk invoice creation and processing</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-outline"
            onClick={() => setViewMode(viewMode === 'dashboard' ? 'list' : 'dashboard')}
          >
            <span className="btn-icon">{viewMode === 'dashboard' ? '📋' : '📊'}</span>
            {viewMode === 'dashboard' ? 'List View' : 'Dashboard View'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setViewMode('form');
            }}
            disabled={!canManageInvoices}
          >
            ➕ New Operation
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="view-tabs">
        <button
          className={`view-tab ${viewMode === 'dashboard' ? 'active' : ''}`}
          onClick={() => setViewMode('dashboard')}
        >
          📊 Dashboard
        </button>
        <button
          className={`view-tab ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          📋 Operations
        </button>
        <button
          className={`view-tab ${viewMode === 'form' ? 'active' : ''}`}
          onClick={() => setViewMode('form')}
        >
          ➕ Create
        </button>
        {viewMode === 'details' && (
          <button
            className={`view-tab active`}
            onClick={() => setViewMode('list')}
          >
            📋 Back to List
          </button>
        )}
      </div>

      {/* Content */}
      <div className="operations-content">
        {viewMode === 'dashboard' && dashboardData && (
          <div className="dashboard-view">
            {/* Operation Statistics */}
            <div className="statistics-section">
              <h3>Operation Statistics</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-content">
                    <h4>Total Operations</h4>
                    <p className="stat-value">{dashboardData.operation_statistics.total_operations}</p>
                  </div>
                </div>
                
                <div className="stat-card pending">
                  <div className="stat-icon">⏳</div>
                  <div className="stat-content">
                    <h4>Pending</h4>
                    <p className="stat-value">{dashboardData.operation_statistics.pending_operations}</p>
                  </div>
                </div>
                
                <div className="stat-card in-progress">
                  <div className="stat-icon">🔄</div>
                  <div className="stat-content">
                    <h4>In Progress</h4>
                    <p className="stat-value">{dashboardData.operation_statistics.in_progress_operations}</p>
                  </div>
                </div>
                
                <div className="stat-card completed">
                  <div className="stat-icon">✅</div>
                  <div className="stat-content">
                    <h4>Completed</h4>
                    <p className="stat-value">{dashboardData.operation_statistics.completed_operations}</p>
                  </div>
                </div>
                
                <div className="stat-card failed">
                  <div className="stat-icon">❌</div>
                  <div className="stat-content">
                    <h4>Failed</h4>
                    <p className="stat-value">{dashboardData.operation_statistics.failed_operations}</p>
                  </div>
                </div>
                
                <div className="stat-card members">
                  <div className="stat-icon">👥</div>
                  <div className="stat-content">
                    <h4>Members Processed</h4>
                    <p className="stat-value">{dashboardData.operation_statistics.total_members_processed}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Operations */}
            <div className="recent-operations-section">
              <h3>Recent Operations</h3>
              <div className="operations-list">
                {dashboardData.recent_operations.map(operation => (
                  <BulkOperationCard
                    key={operation.id}
                    operation={operation}
                    onSelect={() => handleOperationSelect(operation)}
                    onApprove={() => handleOperationApproval(operation.id)}
                    onCancel={() => handleOperationCancellation(operation.id)}
                    canManage={canManageInvoices}
                    isProcessing={isProcessing}
                  />
                ))}
              </div>
            </div>

            {/* Active Operations */}
            {dashboardData.active_operations.length > 0 && (
              <div className="active-operations-section">
                <h3>Active Operations</h3>
                <div className="active-operations-list">
                  {dashboardData.active_operations.map(operation => (
                    <BulkOperationCard
                      key={operation.id}
                      operation={operation}
                      onSelect={() => handleOperationSelect(operation)}
                      onApprove={() => handleOperationApproval(operation.id)}
                      onCancel={() => handleOperationCancellation(operation.id)}
                      canManage={canManageInvoices}
                      isProcessing={isProcessing}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {viewMode === 'list' && (
          <div className="list-view">
            {/* Filters */}
            <div className="filters-section">
              <div className="filters-content">
                <div className="filter-group">
                  <label className="filter-label">Status</label>
                  <select
                    className="filter-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange({ status: e.target.value })}
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                
                <div className="filter-group">
                  <label className="filter-label">Operation Type</label>
                  <select
                    className="filter-select"
                    value={filters.operation_type}
                    onChange={(e) => handleFilterChange({ operation_type: e.target.value })}
                  >
                    <option value="">All Types</option>
                    <option value="annual_renewal">Annual Renewal</option>
                    <option value="new_member">New Member</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                
                <div className="filters-actions">
                  <button
                    className="btn btn-outline"
                    onClick={() => handleFilterChange({
                      status: '',
                      operation_type: '',
                      page: 1
                    })}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Operations List */}
            <div className="operations-grid">
              {operations.map(operation => (
                <BulkOperationCard
                  key={operation.id}
                  operation={operation}
                  onSelect={() => handleOperationSelect(operation)}
                  onApprove={() => handleOperationApproval(operation.id)}
                  onCancel={() => handleOperationCancellation(operation.id)}
                  canManage={canManageInvoices}
                  isProcessing={isProcessing}
                />
              ))}
            </div>

            {/* Empty State */}
            {operations.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">🔄</div>
                <h3>No Operations</h3>
                <p>No bulk invoice operations found for the selected criteria.</p>
              </div>
            )}
          </div>
        )}

        {viewMode === 'form' && (
          <BulkOperationForm
            onSuccess={() => {
              setViewMode('list');
              loadOperations();
            }}
            onCancel={() => {
              setViewMode('list');
            }}
          />
        )}

        {viewMode === 'details' && selectedOperation && (
          <BulkOperationDetails
            operation={selectedOperation}
            onBack={() => setViewMode('list')}
            onApprove={() => handleOperationApproval(selectedOperation.id)}
            onCancel={() => handleOperationCancellation(selectedOperation.id)}
            canManage={canManageInvoices}
            isProcessing={isProcessing}
          />
        )}
      </div>
    </div>
  );
};

export default BulkInvoiceOperations;
